import { extractWaveformPeaks, waveformMask } from './peaks'

// Private types ---------------------------------------------------------------

type DecodedAudio = Readonly<{
  numberOfChannels: number
  getChannelData: (channel: number) => Float32Array
}>

// Public types ----------------------------------------------------------------

export type WaveformResult =
  | Readonly<{ status: 'ready'; mask: string }>
  | Readonly<{ status: 'unavailable' }>

export type WaveformDecodeContext = Readonly<{
  decodeAudioData: (bytes: ArrayBuffer) => Promise<DecodedAudio>
  close: () => Promise<void>
}>

export type WaveformDependencies = Readonly<{
  fetch: (src: string, init: RequestInit) => Promise<Response>
  createContext: () => WaveformDecodeContext | undefined
}>

// Constants -------------------------------------------------------------------

const MAX_DECODE_BYTES = 16 * 1024 * 1024

// Private functions -----------------------------------------------------------

// Avoids connecting a media node or changing the native audio playback graph.
const createDecodeContext = (): WaveformDecodeContext | undefined =>
  typeof AudioContext === 'undefined' ? undefined : new AudioContext()

// Releases a decoder exactly once, including while an uncancellable decode finishes.
const contextCloser = (
  context: WaveformDecodeContext
): (() => Promise<void>) => {
  let closing: Promise<void> | undefined

  return () => {
    closing ??= context.close().catch(() => {})

    return closing
  }
}

// Decodes in an isolated context and drops its sample buffers after extraction.
const decodeWaveform = async (
  context: WaveformDecodeContext,
  bytes: ArrayBuffer,
  signal: AbortSignal
): Promise<WaveformResult> => {
  const close = contextCloser(context)
  const abort = (): void => {
    close()
  }

  signal.addEventListener('abort', abort, { once: true })

  try {
    signal.throwIfAborted()

    const decoded = await context.decodeAudioData(bytes)

    signal.throwIfAborted()

    const channels = Array.from(
      { length: decoded.numberOfChannels },
      (_, index) => decoded.getChannelData(index)
    )

    const result = extractWaveformPeaks(channels)

    return result.status === 'ready'
      ? { status: 'ready', mask: waveformMask(result.peaks) }
      : result
  } finally {
    signal.removeEventListener('abort', abort)
    await close()
  }
}

// Public functions ------------------------------------------------------------

// Treats CORS, HTTP, decode, size, and browser support failures as optional fallback.
export const loadWaveform = async (
  src: string,
  signal: AbortSignal,
  dependencies: WaveformDependencies = {
    fetch: (url, init) => fetch(url, init),
    createContext: createDecodeContext
  }
): Promise<WaveformResult> => {
  try {
    signal.throwIfAborted()

    const response = await dependencies.fetch(src, { signal })

    if (
      !response.ok ||
      Number(response.headers.get('content-length')) > MAX_DECODE_BYTES
    ) {
      await response.body?.cancel()

      return { status: 'unavailable' }
    }

    const bytes = await response.arrayBuffer()

    signal.throwIfAborted()

    if (bytes.byteLength > MAX_DECODE_BYTES) {
      return { status: 'unavailable' }
    }

    const context = dependencies.createContext()

    return context
      ? await decodeWaveform(context, bytes, signal)
      : { status: 'unavailable' }
  } catch {
    return { status: 'unavailable' }
  }
}
